import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useTranslation } from 'react-i18next';
import { Typography, useTheme } from '@mui/material';
import { Pill } from '../../../../components/shared/Pill';
import { TableLoading } from '../../../../components/forms/TableLoading';
import { TableHeader } from '../../../../components/forms/TableHeader';
import { StoreODS } from '../../../../app/services/api/orderManagementClient';
import DatePickerLocalized from '../../../../components/shared/DatePickerLocalized';
import { StoreAvailableODS } from '../../../../app/services/api/orderManagementClient';
import dayjs from 'dayjs';
import { convertFromJsonNetDate } from '../../../../utils';
import { useEffect } from 'react';

export default function StoreODSTable(props: {
  readonly list: StoreODS[],
  readonly isLoading: boolean;
  readonly handleDeliveryDateChange: (item: StoreODS) => void;
  readonly deliverySchedules: StoreAvailableODS[];
  readonly hasWritePermissions: boolean;
  readonly publishDate?: string | null;
}) {
  const { t } = useTranslation('autoshipment');
  const theme = useTheme();

  // Get available dates for a specific store
  const getAvailableDatesForStore = (storeNumber: string) => {
    const storeSchedule = props.deliverySchedules.find(
      schedule => schedule.storeNumber === storeNumber,
    );
    return storeSchedule?.deliveryDates || [];
  };

  // Check if a date should be disabled for a specific store
  const shouldDisableDate = (date: Date, storeNumber: string) => {
    const availableDates = getAvailableDatesForStore(storeNumber);
    if (availableDates.length === 0) return true;

    const dayjsDate = dayjs(date);

    return !availableDates.some((date: string) =>
      dayjsDate.isSame(dayjs(date, 'DD-MM-YYYY'), 'day'));
  };

  // Get default delivery date for a store - first available date that's after publish date
  const getDefaultDeliveryDate = (storeNumber: string) => {
    if (!props.publishDate) return null;
    
    const availableDates = getAvailableDatesForStore(storeNumber);
    if (availableDates.length === 0) return null;
    
    const publishDateObj = dayjs(props.publishDate);

    const availableDatesAfterPublish = availableDates
      .map(dateStr => dayjs(dateStr, 'DD-MM-YYYY'))
      .filter(date => date.isAfter(publishDateObj, 'day'))
      .sort((a, b) => a.valueOf() - b.valueOf());

    const firstAvailableAfterPublish = availableDatesAfterPublish[0];
    
    return firstAvailableAfterPublish ? firstAvailableAfterPublish.format('YYYY-MM-DD') : null;
  };

  useEffect(() => {
    if (!props.publishDate || props.deliverySchedules.length === 0) return;

    props.list?.forEach(store => {
      const existingDate = convertFromJsonNetDate(store.deliveryDate?.toString());
      
      // Only set default if store doesn't have a delivery date
      if (!existingDate) {
        const defaultDate = getDefaultDeliveryDate(store.storeNumber);
        if (defaultDate) {
          const updatedStore = { ...store, deliveryDate: defaultDate };
          props.handleDeliveryDateChange(updatedStore);
        }
      }
    });
  }, [props.publishDate, props.deliverySchedules]);

  return (
    <>
      <TableHeader>
        {t('storesOds')}
        {props.list?.length && <Pill text={`${props.list?.length} item${props.list?.length === 1 ? '' : 's'}`}></Pill>}
      </TableHeader>
      {props.isLoading ? <TableLoading headerHeight={45}
        bodyRowHeight={40}
        minTableWidth={480} /> :
        <TableContainer>
          <Table sx={{ minWidth: '480px' }}>
            <TableHead sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('storeNumber')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('deliveryDate')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {props.list?.map((row: StoreODS) => {
                const currentValue = convertFromJsonNetDate(row.deliveryDate?.toString()) || getDefaultDeliveryDate(row.storeNumber);

                return (
                  <TableRow key={row.customerId}>
                    <TableCell sx={{ width: 200 }}>
                      <Typography variant='textXS'>
                        {row.storeNumber ?? ''} {row.storeName ?? ''}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: 100 }}>
                      <DatePickerLocalized
                        disabled={!props.hasWritePermissions}
                        useCalendar={false}
                        label={t('deliveryDate')}
                        translations={{
                          clearButtonLabel: t('clearButtonLabel'),
                          todayButtonLabel: t('todayButtonLabel'),
                          cancelButtonLabel: t('cancelButtonLabel'),
                          okButtonLabel: t('okButtonLabel'),
                        }}
                        format="DD/MM/YYYY"
                        shouldDisableDate={(date) => shouldDisableDate(new Date(date), row.storeNumber)}
                        value={currentValue}
                        onChange={(newValue) => {
                          if (newValue) {
                            const updatedRow = { ...row, deliveryDate: newValue };
                            props.handleDeliveryDateChange(updatedRow);
                          }
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>}
    </>
  );
}